/**
 * Feedback's files: the picker on the new-request dialog and the comment box,
 * and the list that shows them on a request's thread. The picker and list are
 * the shared ones (ui/Attachments); this holds Feedback's rules: images, PDF,
 * Markdown or text, up to 10 files of 10 MB, and pasted screenshots welcome.
 */

import { attachmentUrl, useRemoveAttachment, useUploadAttachment } from '@/api/queries';
import type { FeedbackAttachment } from '@/api/types';
import {
  AttachmentList as SharedAttachmentList,
  type AttachmentRules,
  useAttachmentDraft as useSharedAttachmentDraft,
} from '@/ui/Attachments';

export { AttachmentPicker, formatBytes } from '@/ui/Attachments';

export const MAX_FILES = 10;

const FEEDBACK_RULES: AttachmentRules = {
  maxFiles: MAX_FILES,
  maxBytes: 10 * 1024 * 1024,
  accept: '.png,.jpg,.jpeg,.gif,.webp,.pdf,.md,.markdown,.txt,image/png,image/jpeg,image/gif,image/webp,application/pdf',
  acceptedNames: /\.(png|jpe?g|gif|webp|pdf|md|markdown|txt)$/i,
  acceptedTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf', 'text/markdown', 'text/plain'],
  hint: 'Or drop files here, or paste a screenshot. Images, PDF, Markdown or text, up to 10 MB each.',
  tooLarge: 'Larger than 10 MB',
  wrongType: 'Not an image, PDF, Markdown or text file',
  screenshots: true,
};

export function useAttachmentDraft() {
  return useSharedAttachmentDraft<FeedbackAttachment>({
    rules: FEEDBACK_RULES,
    upload: useUploadAttachment(),
    remove: useRemoveAttachment(),
  });
}

export function AttachmentList({ attachments }: { attachments: FeedbackAttachment[] }) {
  return <SharedAttachmentList attachments={attachments} urlOf={attachmentUrl} />;
}
